import React, { useState, useEffect } from 'react';
import { PrepAdminLayout } from '../../../components/prep/PrepAdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, value, onChange, multiline, rows = 2, hint, placeholder }) => (
  <div>
    <Label>{label}</Label>
    {hint && <p className="text-xs text-gray-400 mb-1">{hint}</p>}
    {multiline
      ? <Textarea rows={rows} value={value || ''} onChange={e => onChange(e.target.value)} className="mt-1" placeholder={placeholder} />
      : <Input value={value || ''} onChange={e => onChange(e.target.value)} className="mt-1" placeholder={placeholder} />}
  </div>
);

export const PrepAdminIndividualContent = () => {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/prep/content/prep-individual`)
      .then(r => setContent({ ...r.data, individual_items: r.data.individual_items || [] }))
      .catch(console.error);
  }, []);

  const set = (key, val) => setContent(prev => ({ ...prev, [key]: val }));

  const addItem = () => {
    setContent(prev => ({
      ...prev,
      individual_items: [...(prev.individual_items || []), { title: '', price: '', payment_url: '' }]
    }));
  };

  const updateItem = (idx, field, val) => {
    const items = [...(content.individual_items || [])];
    items[idx] = { ...items[idx], [field]: val };
    set('individual_items', items);
  };

  const removeItem = (idx) => {
    const items = [...(content.individual_items || [])];
    items.splice(idx, 1);
    set('individual_items', items);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/prep/content/prep-individual`, { data: content }, { withCredentials: true });
      toast.success('Individual Options page updated');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (!content) return <PrepAdminLayout title="Edit Individual Options"><div className="py-8 text-center text-gray-400">Loading...</div></PrepAdminLayout>;

  return (
    <PrepAdminLayout title="Edit Individual Options Page">
      <div className="max-w-2xl space-y-6">

        <Card className="border-2">
          <CardHeader><CardTitle>Page Hero</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Field label="Page Headline" value={content.hero_headline} onChange={v => set('hero_headline', v)} />
            <Field label="Page Description" value={content.hero_description} onChange={v => set('hero_description', v)} multiline />
          </CardContent>
        </Card>

        <Card className="border-2">
          <CardHeader>
            <CardTitle>Individual Study Materials</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label="Card Title" value={content.materials_title} onChange={v => set('materials_title', v)} />
            <Field label="Card Description" value={content.materials_desc} onChange={v => set('materials_desc', v)} multiline rows={2} />
            <Field label="Note shown below items" value={content.materials_note} onChange={v => set('materials_note', v)} multiline rows={2} />

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold">Material Items</Label>
                <Button size="sm" onClick={addItem} className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus size={14} className="mr-1" /> Add Item
                </Button>
              </div>
              <p className="text-xs text-gray-400 mb-3">
                Each item gets its own "Purchase" button. The payment URL can be a PayPal.me link, Stripe link, or any payment page.
              </p>

              {(content.individual_items || []).length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4 border border-dashed rounded-lg">
                  No items yet. Click "Add Item" to add your first material.
                </p>
              )}

              <div className="space-y-4">
                {(content.individual_items || []).map((item, idx) => (
                  <div key={idx} className="border rounded-lg p-4 bg-gray-50 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-600">Item {idx + 1}</span>
                      <Button size="sm" variant="outline" onClick={() => removeItem(idx)} className="text-red-600 border-red-300 h-7 px-2">
                        <Trash2 size={13} />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Field
                        label="Title"
                        value={item.title}
                        onChange={v => updateItem(idx, 'title', v)}
                        placeholder="e.g. Video Lesson: CPR Basics"
                      />
                      <Field
                        label="Price"
                        value={item.price}
                        onChange={v => updateItem(idx, 'price', v)}
                        placeholder="e.g. $20"
                      />
                    </div>
                    <Field
                      label="Payment URL"
                      value={item.payment_url}
                      onChange={v => updateItem(idx, 'payment_url', v)}
                      placeholder="https://paypal.me/yourname/20 or any payment link"
                    />
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2">
          <CardHeader><CardTitle>One-on-One Tutoring Sessions</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Field label="Card Title" value={content.sessions_title} onChange={v => set('sessions_title', v)} />
            <Field label="Card Description" value={content.sessions_desc} onChange={v => set('sessions_desc', v)} multiline rows={2} />
            <Field label="Session Options (one per line)" value={content.sessions_list} onChange={v => set('sessions_list', v)} multiline rows={5}
              hint="e.g. 60-minute sessions: $90" />
            <Field label="Bottom Note" value={content.sessions_note} onChange={v => set('sessions_note', v)} multiline rows={2} />
            <div>
              <Label>Session Booking URL</Label>
              <p className="text-xs text-gray-400 mb-1">Paste your booking link (PayPal.me, Zoho, Calendly, etc.). Leave blank to use Contact page.</p>
              <Input
                value={content.sessions_booking_url || ''}
                onChange={e => set('sessions_booking_url', e.target.value)}
                className="mt-1"
                placeholder="https://paypal.me/yourname or https://zbooking.us/xxx"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2">
          <CardHeader><CardTitle>Bottom CTA</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Field label="Headline" value={content.cta_headline} onChange={v => set('cta_headline', v)} />
            <Field label="Description" value={content.cta_description} onChange={v => set('cta_description', v)} multiline rows={2} />
          </CardContent>
        </Card>

        <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white w-full">
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>
    </PrepAdminLayout>
  );
};
